import React, { useEffect, useState } from "react";
import Button from "../../components/form/Button";
import Input from "../../components/form/Input";
import Tabs from "../../components/elements/Tabs";
import Image from "../../components/elements/Image";
import PayCustomAmount from "../../components/sections/SplitBillModals/PayCustomAmount";
import PayForYourItem from "../../components/sections/SplitBillModals/PayForYourItem";
import DivideBillEqualy from "../../components/sections/SplitBillModals/DivideBillEqualy";
import { Link, useParams } from 'react-router-dom';
import siteLogo from "../../assets/images/logo.png";

import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import CheckoutForm from "../components/stripe/CheckoutForm";
import { apiRequest } from "../../util/apiUtils";
import { EmptyBillingState, BillItemSkeleton } from "../components/loaders/MenuSkeletons";
//import PaymentForm from "../components/stripe/PaymentForm";

const stripePromise = loadStripe("pk_test_51SOCw47rV8gdwKZcKmtSE04aitFEZ5loPR5p5efhSsHAa28YdC4Qj0qAK47mitwHE9DERHbz9wBK4XZjNlpNpOAL00bx7Hh4Le");

// Skeleton Loaders
const BillPaySkeleton = () => (
  <div className="w-full max-w-sm mx-auto bg-white rounded-2xl shadow-sm overflow-hidden pb-6">
    {/* Header Skeleton */}
    <div className="border-b border-[#F4F4F4] px-4 py-5">
      <div className="flex items-center justify-between gap-4">
        <div className="w-10 h-10 bg-gray-200 rounded animate-pulse"></div>
        <div className="flex-1">
          <div className="h-5 bg-gray-200 rounded w-24 mb-1 animate-pulse"></div>
        </div>
        <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
      </div>
    </div>

    {/* Items Skeleton */}
    <div className="my-3 border-b border-gray-200 px-4 py-3">
      <BillItemSkeleton />
      <BillItemSkeleton />
      <BillItemSkeleton />
    </div>

    {/* Total Skeleton */}
    <div className="px-4 mb-4">
      <div className="flex justify-between items-center">
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-32 mb-1 animate-pulse"></div>
          <div className="h-3 bg-gray-200 rounded w-40 animate-pulse"></div>
        </div>
        <div className="h-6 bg-gray-200 rounded w-16 animate-pulse"></div>
      </div>
    </div>

    {/* Tabs Skeleton */}
    <div className="px-4 mb-4">
      <div className="flex gap-3">
        <div className="flex-1 h-12 bg-gray-200 rounded-full animate-pulse"></div>
        <div className="flex-1 h-12 bg-gray-200 rounded-full animate-pulse"></div>
      </div>
    </div>

    {/* Content Skeleton */}
    <div className="px-4">
      <div className="rounded-xl border border-gray-200 p-4 bg-slate-50/30">
        <div className="h-4 bg-gray-200 rounded w-48 mb-3 animate-pulse"></div>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="aspect-square bg-gray-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
        <div className="h-10 bg-gray-200 rounded-lg animate-pulse"></div>
      </div>
    </div>
  </div>
);

const BillPay = () => {
  const params = useParams();
  const tableNumber = params?.table;
  const orderId = params?.orderid;
  
  const [selectedTip, setSelectedTip] = useState(0);
  const [customTip, setCustomTip] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [openCustom, setOpenCustom] = useState(false);
  const [openItems, setOpenItems] = useState(false);
  const [openEqual, setOpenEqual] = useState(false);

  const [paymentType, setPaymentType] = useState("full");
  const [orderDetails, setOrderDetails] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [splitProcess, setSplitProcess] = useState('');

  const tipAmount = customTip ? Number(customTip) : (selectedTip || 0);

  // Check if any split option has been selected
  const hasSplitSelected = splitProcess && ['custom_amount', 'split_item', 'split_amount'].includes(splitProcess);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      setLoading(true);
      try {
        const result = await apiRequest("post", `order/order-detail`, {
          body: {
            tableNumber,
            orderId              
          },
        });
        if (result.status === 200) {
          setOrderDetails(result?.data?.data || {});
          setTotal(result?.data?.data?.billAmount || 0);
          
          const splitType = result?.data?.data?.payment?.splitType;
          setSplitProcess(splitType || '');
          
          // If split type exists and is not 'full', set active tab to Split Bill
          if (splitType && splitType !== 'full') {
            setActiveTab(1);
            setPaymentType('split');
          } else {
            setActiveTab(0);
            setPaymentType('full');
          }
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
      } finally {
        setLoading(false);
      }
    };    
    fetchOrderDetails();
  }, [tableNumber, orderId]);  

  const handleTipSelect = (value) => {
    // If clicking the same tip, deselect it
    if (value === selectedTip && value !== null) {
      setSelectedTip(0);
      setCustomTip("");
      return;
    }
    
    setSelectedTip(value);
    
    // Clear custom tip when selecting predefined tip
    if (value !== null) {
      setCustomTip("");
    }
  };

  const handleCustomTipChange = (e) => {
    const value = e.target.value;
    setCustomTip(value);
    // When typing custom tip, deselect predefined tips
    if (value) {
      setSelectedTip(null);
    }
  };  

  const payableAmount = total + (customTip ? Number(customTip) : (selectedTip || 0));

  const PaymentContent = () => (
    <div className="mt-0">
      <div className="rounded-xl border border-gray-200 px-3 shadow-sm pt-5 pb-3 bg-slate-50/30">
        <p className="font-semibold text-gray-900 text-sm mb-3">
          Would you like to add tip?
        </p>

        <div className="grid grid-cols-4 gap-2">
          {[5, 10, 15].map((tip) => (
            <Button
              key={tip}
              buttonLabel={`$ ${tip}`}
              buttonFunction={() => handleTipSelect(tip)}
              buttonClasses={`
                w-full aspect-square rounded-xl border text-sm font-semibold
                flex items-center justify-center transition-all h-auto
                ${selectedTip === tip
                  ? "bg-[#F3E8FF] border-[#C084FC] text-[#9333EA]" 
                  : "bg-white border-gray-300 text-gray-800 hover:bg-gray-50"}
              `}
            />
          ))}

          <Button
            buttonFunction={() => handleTipSelect(null)}
            buttonClasses={`
              w-full aspect-square rounded-xl border text-sm font-semibold
              flex flex-col items-center justify-center transition-all gap-1 h-auto
              ${selectedTip === null
                ? "bg-[#F3E8FF] border-[#C084FC] text-[#9333EA]"
                : "bg-white border-gray-300 text-gray-800 hover:bg-gray-50"}
            `}
            buttonLabel={
              <>
                <i className="fa-regular fa-pen text-xs"></i>
                <span>custom</span>
              </>
            }
          />
        </div>

        {selectedTip === null && (
          <div className="mt-3">
            <Input
              inputPlaceholder="Enter custom tip amount"
              inputType="number"
              value={customTip}
              onChange={(e) => setCustomTip(e.target.value)}
              inputClasses="h-9 text-sm"
            />
          </div>
        )}

        <p className="mt-3 text-sm text-gray-900 font-semibold">
          You are paying $ {payableAmount > 0 ? payableAmount.toFixed(2) : "0.00"}
        </p>

        {/* Card Section */}
        <Elements stripe={stripePromise}>
          <CheckoutForm 
            paymentType={paymentType} 
            tableNumber={tableNumber}
            orderId={orderId} 
            tipAmount={tipAmount}            
          />
        </Elements>

        <p className="text-[10px] text-gray-500 mt-3 text-center flex items-center justify-center gap-1">
          <i className="fa-solid fa-shield-check text-green-600"></i>
          100% Secure payments
        </p>
      </div>
    </div>
  );

  const SplitBillContent = () => (
    <div className="mt-0">
      <div className="rounded-xl border border-gray-200 px-3 shadow-sm pt-5 pb-3 bg-slate-50/30">
        <p className="font-semibold text-gray-900 text-sm mb-3">
          Would you like to add tip?
        </p>

        <div className="grid grid-cols-4 gap-2">
          {[5, 10, 15].map((tip) => (
            <Button
              key={tip}
              buttonLabel={`$ ${tip}`}
              buttonFunction={() => handleTipSelect(tip)}
              buttonClasses={`
                w-full aspect-square rounded-xl border text-sm font-semibold
                flex items-center justify-center transition-all h-auto
                ${selectedTip === tip
                  ? "bg-[#F3E8FF] border-[#C084FC] text-[#9333EA]"
                  : "bg-white border-gray-300 text-gray-800 hover:bg-gray-50"}
              `}
            />
          ))}

          <Button
            buttonFunction={() => handleTipSelect(null)}
            buttonClasses={`
              w-full aspect-square rounded-xl border text-sm font-semibold
              flex flex-col items-center justify-center transition-all gap-1 h-auto
              ${selectedTip === null
                ? "bg-[#F3E8FF] border-[#C084FC] text-[#9333EA]"
                : "bg-white border-gray-300 text-gray-800 hover:bg-gray-50"}
            `}
            buttonLabel={
              <>
                <i className="fa-regular fa-pen text-xs"></i>
                <span>custom</span>
              </>
            }
          />
        </div>

        {selectedTip === null && (
          <div className="mt-3">
            <Input
              inputPlaceholder="Enter custom tip amount"
              inputType="number"
              inputValue={customTip}
              onChange={(e) => setCustomTip(e.target.value)}
              inputClasses="h-9 text-sm"
            />
          </div>
        )}

        <div className="mt-4">
          <h5 className="text-lg text-[#232222] font-medium mb-1">Split the bill</h5>
          <p className="text-base text-[#9FA1A3] mb-4 font-normal">
            You can pay for your share by choosing your preferred payment method below.
          </p>

          <div className="flex flex-col gap-2">
            {/* Show all options if no split selected, otherwise show only the selected one */}
            {(!hasSplitSelected || splitProcess === 'custom_amount') && (
              <Button
                buttonLabel="Pay a custom amount"
                buttonClasses="w-full bg-[#F5F5F5] border border-[#D8D8D8] text-[14px] rounded-lg px-4 transition-all justify-start h-12 hover:bg-white"
                buttonLabelClasses="text-base font-semibold text-gray-900 text-left"
                buttonFunction={() => setOpenCustom(true)}
              />
            )}
            
            {(!hasSplitSelected || splitProcess === 'split_item') && (
              <Button
                buttonLabel="Pay for your items"
                buttonClasses="w-full bg-[#F5F5F5] border border-[#D8D8D8] text-[14px] rounded-lg px-4 transition-all justify-start h-12 hover:bg-white"
                buttonLabelClasses="text-base font-semibold text-gray-900 text-left"
                buttonFunction={() => setOpenItems(true)}
              />
            )}
            
            {(!hasSplitSelected || splitProcess === 'split_amount') && (
              <Button
                buttonLabel="Divide the bill equally"
                buttonClasses="w-full bg-[#F5F5F5] border border-[#D8D8D8] text-[14px] rounded-lg px-4 transition-all justify-start h-12 hover:bg-white"
                buttonLabelClasses="text-base font-semibold text-gray-900 text-left"
                buttonFunction={() => setOpenEqual(true)}
              />
            )}
          </div>

          {/* Show info message when a split option is locked */}
          {hasSplitSelected && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
              <i className="fa-solid fa-info-circle text-blue-600 text-sm mt-0.5"></i>
              <p className="text-xs text-blue-800">
                You've selected a split payment option. Complete this payment to unlock other options.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Show skeleton while loading
  if (loading) {
    return <BillPaySkeleton />;
  }  

  

  return (
    <>    
      <div className="w-full max-w-sm mx-auto bg-white rounded-2xl shadow-sm overflow-hidden pb-6">
        {/* Header - Always visible */}
        <div className="border-b border-[#F4F4F4] flex-shrink-0">
          <div className="flex items-center justify-between gap-4 px-4 py-5">
            <Link 
              to={(tableNumber && tableNumber !== undefined)?`/bistro/home/table/${tableNumber}`:`/bistro/home`} 
              className="w-10 aspect-square overflow-hidden flex-shrink-0"
            >
              <Image src={siteLogo} classes={"object-cover"} />
            </Link>
            <div className="relative flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 text-base sm:text-lg">
                Table {(Object.keys(orderDetails).length > 0) ? orderDetails?.tableData?.tableNumber : ''}
              </h3>
            </div>
            <Button
              buttonLabel={"View Menu"}
              buttonLabelClasses={"font-medium text-sm text-black"}
              buttonClasses={"bg-transparent p-0 h-5 hover:bg-transparent flex-shrink-0"}
              buttonHasLink={true}
              buttonLink={(tableNumber && tableNumber !== undefined)?`/bistro/menu/table/${tableNumber}`:`/bistro/menu`}
            />
          </div>
        </div>

        {/* Empty State - Shows below header when no order */}
        {(!orderDetails || Object.keys(orderDetails).length === 0) ? (
          <div className="min-h-[60vh] flex items-center justify-center">
            <EmptyBillingState
              search={loading}
              tableNumber={tableNumber}
            />
          </div>
        ) : (
          <>
            {/* Order Items */}
            <div className="my-3 border-b border-gray-200 w-full">
              <div className="px-4 my-3">
                {orderDetails?.items?.length > 0 && orderDetails.items.map((item, index) => (
                  <div 
                    key={index} 
                    className="flex justify-between items-center py-2 text-sm sm:text-base"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="bg-gray-100 text-gray-700 text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                        {item.qty}x
                      </span>
                      <span className="text-sm font-medium text-black">
                        {item?.productName}
                        {item?.variantName ? ` - ${item?.variantName}` : ''}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-black flex-shrink-0 ml-2">
                      ${item?.price?.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Payable Amount */}
            <div className="px-4 mb-4">
              <div className="flex justify-between items-center">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm">Payable amount</p>
                  <p className="text-[10px] text-gray-500">Inclusive of all taxes and charges</p>
                </div>
                <p className="text-lg font-bold text-gray-900 flex-shrink-0 ml-4">
                  ${orderDetails?.billAmount?.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Payment Tabs */}
            <div className="px-4">
              <Tabs
                selectedIndex={activeTab}
                setSelectedIndex={(e) => {
                  // Only allow tab change if no split option is selected
                  if (!hasSplitSelected) {
                    setActiveTab(e);
                    setSelectedTip(0);
                    setCustomTip("");
                    if(e === 0){
                      setPaymentType('full');
                      setSplitProcess('');
                    } else {
                      setPaymentType('split');
                    }
                  }
                }}
                content={true}
                tabsClass="flex w-full justify-between gap-3 mb-4"
                items={[
                  {
                    label: (
                      <span
                        className={`w-full block text-center py-3 rounded-full border text-sm font-semibold transition-all px-14 ${
                          activeTab === 0
                            ? "bg-black text-white border-black"
                            : hasSplitSelected
                            ? "bg-gray-200 text-gray-400 border-gray-300 cursor-not-allowed"
                            : "bg-white text-black border-gray-300 cursor-pointer"
                        }`}
                      >
                        Pay Fully
                      </span>
                    ),
                    content: <PaymentContent />,
                    disabled: hasSplitSelected // Disable Pay Fully tab if split option selected
                  },
                  {
                    label: (
                      <span
                        className={`w-full block text-center py-3 rounded-full border text-sm font-semibold transition-all px-14 ${
                          activeTab === 1
                            ? "bg-black text-white border-black"
                            : "bg-white text-black border-gray-300 cursor-pointer"
                        }`}
                      >
                        Split Bill
                      </span>
                    ),
                    content: <SplitBillContent />,
                  },
                ]}                
              />
            </div>
          </>
        )}
      </div>

      {/* Modals */}      
      <PayCustomAmount 
        isOpen={openCustom} 
        onClose={() => setOpenCustom(false)} 
        total={total} 
        selectedTip={selectedTip} 
        stripePromise={stripePromise}
        paymentType={'custom_amount'} 
        tableNumber={tableNumber}
        orderId={orderId}
        customTip={customTip}
        previousTransactions = {orderDetails?.miniStatement}
      />    
      <PayForYourItem 
        isOpen={openItems} 
        onClose={() => setOpenItems(false)} 
        total={total} 
        selectedTip={selectedTip} 
        stripePromise={stripePromise}
        paymentType={'split_item'}
        tableNumber={tableNumber}
        orderId={orderId}
        customTip={customTip}
        orderItems={orderDetails?.items || []}
        previousTransactions = {orderDetails?.miniStatement}
        paymentData={orderDetails?.payment}
      />    
      <DivideBillEqualy 
        isOpen={openEqual} 
        onClose={() => setOpenEqual(false)} 
        total={total} 
        selectedTip={selectedTip} 
        stripePromise={stripePromise}
        paymentType={'split_amount'}
        tableNumber={tableNumber}
        orderId={orderId}
        customTip={customTip}
        previousTransactions={orderDetails?.miniStatement}
        numberOfPerson={orderDetails?.numberOfPerson}
        paymentData={orderDetails?.payment}
      />           
    </>
  );
};

export default BillPay;